import React, { useMemo, useCallback, useRef, useEffect } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  ColumnSeries,
  Tooltip,
  Inject,
  Category,
} from "@syncfusion/ej2-react-charts";
import { formatNumber } from "../../utils/formatNumber";
import "./Chart.css";

function parseHourFromLabel(label) {
  const m = String(label || "").match(/(\d{2})\s*:\s*\d{2}/);
  if (!m) return null;
  const hh = Number(m[1]);
  return Number.isFinite(hh) ? hh : null;
}

function inferDaysCountFromLabels(labels) {
  const days = new Set();
  for (const l of labels || []) {
    const m = String(l || "").match(/^(\d{2}[./-]\d{2})\b/);
    if (m?.[1]) days.add(m[1]);
  }
  return days.size || 1;
}

export const OverviewSwitchInChart = ({
  series = [],
  seriesWeekBefore = [],
  showComparison = false,
  chartType = "bar",
  title = "Switch-In",
  loading = false,
  mode = "hour",
}) => {
  const chartRef = useRef(null);

  const storedUser = JSON.parse(
    localStorage.getItem("adm_panel_user") || "{}"
  );
  const isDark = !!storedUser.darkmode;

  const axisTextColor = isDark ? "#ffffff" : "#666666";
  const subTitleColor = isDark ? "#aaaaaa" : "#666666";

  const { chartData, chartDataWeekBefore, yAxisMax, labels } = useMemo(() => {
    const clean = (Array.isArray(series) ? series : []).map((p) => ({
      x: String(p?.label ?? ""),
      y: Number(p?.value) || 0,
    }));

    const cleanWeekBefore = (Array.isArray(seriesWeekBefore)
      ? seriesWeekBefore
      : []
    ).map((p) => ({
      x: String(p?.label ?? ""),
      y: Number(p?.value) || 0,
    }));

    const max = Math.max(
      ...clean.map((d) => d.y),
      ...(showComparison ? cleanWeekBefore.map((d) => d.y) : []),
      0
    );

    return {
      chartData: clean,
      chartDataWeekBefore: cleanWeekBefore,
      labels: clean.map((d) => d.x),
      yAxisMax: max > 0 ? Math.ceil(max * 1.15) : 10,
    };
  }, [series, seriesWeekBefore, showComparison]);

  /* ✅ RESIZE FIX */
  useEffect(() => {
    if (!chartRef.current) return;

    const chartInstance = chartRef.current;
    const parentElement = chartInstance.element?.parentElement;
    if (!parentElement) return;

    const resizeObserver = new ResizeObserver(() => {
      chartInstance.refresh();
    });

    resizeObserver.observe(parentElement);

    return () => resizeObserver.disconnect();
  }, []);

  const hourStep = useMemo(() => {
    if (mode !== "hour") return 1;
    const daysCount = inferDaysCountFromLabels(labels);
    if (daysCount === 1) return 1;
    return daysCount <= 7 ? 6 : 8;
  }, [mode, labels]);

  const handleAxisLabelRender = useCallback(
    (args) => {
      if (args.axis?.name === "primaryYAxis") {
        const value = parseFloat(args.value);
        if (!isNaN(value)) args.text = formatNumber(value);
        return;
      }

      if (args.axis?.name !== "primaryXAxis") return;

      const label = String(args.text ?? "");

      if (mode === "hour") {
        const hh = parseHourFromLabel(label);
        if (!Number.isFinite(hh) || hh % hourStep !== 0) {
          args.text = "";
        }
      }
    },
    [mode, hourStep]
  );

  const handleTooltipRender = useCallback(
    (args) => {
      if (!args.point) return;
      args.text = `${args.point.x} : ${formatNumber(args.point.y)}`;
    },
    []
  );

  return (
    <>
      <h3 className="chart-title">{title}</h3>
      <p
        style={{
          marginTop: "-8px",
          marginBottom: "12px",
          fontSize: "14px",
          color: subTitleColor,
        }}
      >
        {showComparison ? "Yesterday vs Week Before" : "Yesterday"}
      </p>

      {!loading && series?.length > 0 && (
        <ChartComponent
          ref={chartRef}
          width="100%"
          height="420px"
          legendSettings={{ visible: false }}
          primaryXAxis={{
            valueType: "Category",
            rangePadding: "Additional",
            majorGridLines: { width: 0 },
            majorTickLines: { width: 0 },
            labelStyle: { color: axisTextColor },
          }}
          primaryYAxis={{
            minimum: 0,
            maximum: yAxisMax,
            majorTickLines: { width: 0 },
            lineStyle: { width: 0 },
            labelStyle: { color: axisTextColor },
          }}
          tooltip={{ enable: true }}
          axisLabelRender={handleAxisLabelRender}
          tooltipRender={handleTooltipRender}
        >
          <Inject services={[ColumnSeries, Category, Tooltip]} />

          <SeriesCollectionDirective>
            <SeriesDirective
              dataSource={chartData}
              xName="x"
              yName="y"
              type="Column"
              fill="#1976d2"
              animation={{ enable: false }}
            />

            {showComparison && chartDataWeekBefore.length > 0 && (
              <SeriesDirective
                dataSource={chartDataWeekBefore}
                xName="x"
                yName="y"
                type="Column"
                fill="#e91e63"
                animation={{ enable: false }}
              />
            )}
          </SeriesCollectionDirective>
        </ChartComponent>
      )}
    </>
  );
};

export default OverviewSwitchInChart;
